/**
 * PolicyEditorSectionsA — the Identity and Routing intent tabs of the policy form.
 * Template example: NextAdmin `profile/account/page.tsx` + demo `/form-layouts` (FormSection two-column grid).
 */
import type React from 'react';
import {
  FormFullWidth,
  FormSection,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
} from '../../../components/form/index.js';
import { TagsFormField } from './FieldPrimitives.js';
import { RUNTIME_POLICY_FIELD_HELP } from '../../../help/runtime-policy-fields.js';
import { COST_TIERS, LATENCY_CLASSES, PERMISSION_TIERS, PURPOSES, RISK_TIERS, STATUSES, enumOptions } from './types.js';

export interface SectionProps {
  /** The policy does not exist yet: the slug can still be chosen. */
  isCreating: boolean;
  /** The person may change runtime policies; otherwise every field is read-only. */
  canEdit: boolean;
}

export const IdentitySection: React.FC<SectionProps> = ({ isCreating, canEdit }) => (
  <FormSection
    title="Identity and lifecycle"
    description="How people recognise this policy and whether it can be chosen."
  >
    <TextFormField
      name="name"
      help={RUNTIME_POLICY_FIELD_HELP.name}
      label="Name"
      required
      disabled={!canEdit}
      placeholder="Interactive default"
    />
    <TextFormField
      name="slug"
      help={RUNTIME_POLICY_FIELD_HELP.slug}
      label="Slug"
      required
      disabled={!canEdit || !isCreating}
      placeholder="interactive-default"
      description={isCreating ? 'Lowercase letters and hyphens. It cannot be changed after creation.' : undefined}
    />
    <SelectFormField
      name="status"
      help={RUNTIME_POLICY_FIELD_HELP.status}
      label="Status"
      disabled={!canEdit}
      options={enumOptions(STATUSES)}
      description="Only active policies can be chosen. Archiving a policy moves its users to the default."
    />
    <FormFullWidth>
      <TextAreaFormField
        name="description"
        help={RUNTIME_POLICY_FIELD_HELP.description}
        label="Description"
        rows={3}
        disabled={!canEdit}
        placeholder="What this policy is for"
      />
    </FormFullWidth>
  </FormSection>
);

export const RoutingSection: React.FC<SectionProps> = ({ canEdit }) => (
  <FormSection title="Use-case matching" description="How the resolver selects this policy.">
    <SelectFormField
      name="purpose"
      help={RUNTIME_POLICY_FIELD_HELP.purpose}
      label="Purpose"
      disabled={!canEdit}
      options={enumOptions(PURPOSES)}
    />
    <SelectFormField
      name="riskTier"
      help={RUNTIME_POLICY_FIELD_HELP.riskTier}
      label="Risk tier"
      disabled={!canEdit}
      options={enumOptions(RISK_TIERS)}
    />
    <SelectFormField
      name="latencyClass"
      help={RUNTIME_POLICY_FIELD_HELP.latencyClass}
      label="Latency class"
      disabled={!canEdit}
      options={enumOptions(LATENCY_CLASSES)}
    />
    <SelectFormField
      name="costTier"
      help={RUNTIME_POLICY_FIELD_HELP.costTier}
      label="Cost tier"
      disabled={!canEdit}
      options={enumOptions(COST_TIERS)}
    />
    <SelectFormField
      name="permissionTier"
      help={RUNTIME_POLICY_FIELD_HELP.permissionTier}
      label="Permission tier"
      disabled={!canEdit}
      options={enumOptions(PERMISSION_TIERS)}
    />
    <TagsFormField
      name="tags"
      help={RUNTIME_POLICY_FIELD_HELP.tags}
      label="Tags"
      disabled={!canEdit}
      placeholder="Add a tag and press Enter"
    />
  </FormSection>
);
